import { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Col, Descriptions, Input, Modal, Row, Tag, Form, Checkbox, Space } from "antd";
import { CloseOutlined, EditOutlined, SendOutlined, UndoOutlined, SaveOutlined, PrinterOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";
import html2canvas from "html2canvas";
import { ContentHeader } from "../../components";
import { maChucVu, responseStatus, urlAdmin } from "../../constants";
import { Links } from "../../helpers/Links";
import { dateCommon } from "../../helpers/common";
import { DeXuatNhuCauTaiSan, LoaiNhuCauTaiSan, TrangThaiDeXuatNhuCau } from "../../models/taisan/DeXuatNhuCauTaiSan";
import LoadingDetailPage from "../../modules/loading/LoadingDetailPage";
import { layChiTietDeXuat, pheDuyetDeXuat, thuHoiDeXuat, trinhDuyetDeXuat, tuChoiDeXuat, chuyenCVVPDeXuat, luuDeXuat, trinhThamMuuDeXuat, luuFileInDeXuat } from "../../services/deXuatNhuCauTaiSan";
import { layThongTinCauHinh } from "../../services/setting";
import { layDsNhanVien } from "../../services/nhanvien";
import NhanVienPhongBanSelectBox from "../utils/NhanVienPhongBanSelectBox";

// Id đơn vị Văn phòng (tương ứng Constants.IdDonVi.VanPhong ở backend)
const ID_DON_VI_VAN_PHONG = 10;

interface ModalTextFormProps {
    open: boolean;
    title: string;
    onOk: (value: string) => void;
    onCancel: () => void;
    placeholder?: string;
    batBuocLyDo?: boolean;
}

const ModalTextForm = ({ open, title, onOk, onCancel, placeholder, batBuocLyDo }: ModalTextFormProps) => {
    const [value, setValue] = useState("");

    useEffect(() => {
        if (open) {
            setValue("");
        }
    }, [open]);

    const handleOk = () => {
        if (batBuocLyDo && !value.trim()) {
            toast.warning("Vui lòng nhập lý do");
            return;
        }
        onOk(value.trim());
    };

    return (
        <Modal
            title={title}
            open={open}
            onOk={handleOk}
            onCancel={onCancel}
            okText="Đồng ý"
            cancelText="Hủy"
        >
            <label>Lý do {batBuocLyDo && <span className="text-red">*</span>}</label>
            <Input.TextArea
                rows={4}
                placeholder={placeholder || "Nhập nội dung"}
                value={value}
                onChange={(e) => setValue(e.target.value)}
            />
        </Modal>
    );
};

const XemDeXuatNhuCau = () => {
    const navigate = useNavigate();
    const user = useSelector((state: any) => state.auth.currentUser);
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<DeXuatNhuCauTaiSan | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [currentAction, setCurrentAction] = useState<"approve" | "reject" | null>(null);
    
    // Advisory form state
    const [form] = Form.useForm();
    const [advisoryContent, setAdvisoryContent] = useState("");
    const [phoCVPChecked, setPhoCVPChecked] = useState(false);
    const [cvpChecked, setCVPChecked] = useState(false);
    const [phoGDChecked, setPhoGDChecked] = useState(false);
    const [gdChecked, setGDChecked] = useState(false);
    const [phoCVPInfo, setPhoCVPInfo] = useState<any>(null);
    const [cvpInfo, setCVPInfo] = useState<any>(null);
    const [phoGDInfo, setPhoGDInfo] = useState<any>(null);
    const [gdInfo, setGDInfo] = useState<any>(null);
    const [isSavingAdvisory, setIsSavingAdvisory] = useState(false);

    // Chọn CV VP tham mưu (Chánh VP chọn cụ thể cho từng đợt đề xuất)
    const [cvvpModalOpen, setCVVPModalOpen] = useState(false);
    const [selectedCVVP, setSelectedCVVP] = useState<number | undefined>(undefined);
    const [isTransferringCVVP, setIsTransferringCVVP] = useState(false);

    // In đề xuất nhu cầu tài sản
    const printRef = useRef<any>();
    const [isPrinting, setIsPrinting] = useState(false);
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `DeXuatNhuCauTaiSan_${data?.id}_${new Date().getTime()}`,
        pageStyle: `
            @page {
                size: A4 portrait !important;
                margin: 0 !important;
            }
            html, body {
                width: 210mm !important;
                height: auto !important;
            }
        `,
        onAfterPrint: async () => {
            await handleCapturePrint();
        },
    });

    const handleClickInButton = () => {
        Modal.confirm({
            title: "Xác nhận in",
            content: "Bạn có muốn in đề xuất nhu cầu tài sản này không?",
            okText: "In",
            cancelText: "Hủy",
            onOk: () => {
                handlePrint();
            },
        });
    };

    const handleCapturePrint = async () => {
        if (!printRef.current || !data?.id) return;
        try {
            setIsPrinting(true);
            const canvas = await html2canvas(printRef.current, { scale: 2 });
            const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
            if (!blob) return;
            const file = new File([blob], `DeXuatNhuCauTaiSan_${data.id}.png`, { type: "image/png" });
            const response = await luuFileInDeXuat(data.id, file);
            if (response.trangThai === responseStatus.thanhCong) {
                toast.success("Lưu file in thành công");
                loadData();
            } else {
                toast.error(response.thongBao || "Lưu file in không thành công");
            }
        } catch (error: any) {
            toast.error(error.message || "Lỗi khi lưu file in");
        } finally {
            setIsPrinting(false);
        }
    };

    const loadData = async () => {
        if (!id) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const result = await layChiTietDeXuat(parseInt(id));
            setData(result);
            
           // Reset advisory form
           setAdvisoryContent(result?.noiDungThamMuu || "");
           setPhoCVPChecked((result?.idPhoCVP || 0) > 0);
           setCVPChecked((result?.idCVP || 0) > 0);
           setPhoGDChecked((result?.idPhoGiamDoc || 0) > 0);
           setGDChecked((result?.idGiamDoc || 0) > 0);
            
           // Load config
           const [cauHinh, dsNhanVien] = await Promise.all([
               layThongTinCauHinh(),
               layDsNhanVien(true)
           ]);
           const timNhanVien = (id?: number) => {
               if (!id) return null;
               const nv = dsNhanVien.find(x => x.value === id);
               return nv ? { id: nv.value, hoTen: nv.label } : null;
           };
            
           setPhoCVPInfo(
               timNhanVien(cauHinh.duyetNhuCauTaiSan_PhoCVP) 
               ?? (result?.idPhoCVP ? { id: result?.idPhoCVP, hoTen: result?.tenPhoCVP } : null)
           );
           setCVPInfo(
               timNhanVien(cauHinh.duyetNhuCauTaiSan_CVP) 
               ?? (result?.idCVP ? { id: result?.idCVP, hoTen: result?.tenCVP } : null)
           );
           setPhoGDInfo(
               timNhanVien(cauHinh.duyetNhuCauTaiSan_PhoGD) 
               ?? (result?.idPhoGiamDoc ? { id: result?.idPhoGiamDoc, hoTen: result?.tenPhoGiamDoc } : null)
           );
           setGDInfo(
               timNhanVien(cauHinh.duyetNhuCauTaiSan_GD) 
               ?? (result?.idGiamDoc ? { id: result?.idGiamDoc, hoTen: result?.tenGiamDoc } : null)
           );

       } catch (error: any) {
           toast.error(error.message || "Lỗi khi tải dữ liệu");
           navigate(Links.DeXuatNhuCauTaiSan.DanhSach);
       } finally {
           setLoading(false);
       }
    };

    useEffect(() => {
        loadData();
    }, [id]);

    const getTenLoaiNhuCau = (value?: number) => {
        switch (value) {
            case LoaiNhuCauTaiSan.MuaMoi:
                return "Mua mới";
            case LoaiNhuCauTaiSan.BanGiao:
                return "Bàn giao";
            case LoaiNhuCauTaiSan.SuaChua:
                return "Sửa chữa";
            case LoaiNhuCauTaiSan.DieuChuyen:
                return "Điều chuyển";
            default:
                return "Không xác định";
        }
    };

    const getTrangThaiInfo = (value?: number) => {
        switch (value) {
            case TrangThaiDeXuatNhuCau.KhoiTao:
                return { label: "Khởi tạo", color: "default" };
            case TrangThaiDeXuatNhuCau.ThuHoi:
                return { label: "Thu hồi", color: "red" };
            case TrangThaiDeXuatNhuCau.TrinhLanhDaoPhong:
                return { label: "Trình LĐ Phòng", color: "blue" };
            case TrangThaiDeXuatNhuCau.LanhDaoPhongTraLai:
                return { label: "LĐ Phòng trả lại", color: "orange" };
            case TrangThaiDeXuatNhuCau.ChuyenChanhVP:
                return { label: "Chuyển Chánh VP", color: "blue" };
            case TrangThaiDeXuatNhuCau.ChanhVPTraLai:
                return { label: "Chánh VP trả lại", color: "orange" };
            case TrangThaiDeXuatNhuCau.CVVPThamMuu:
                return { label: "CV VP tham mưu", color: "cyan" };
            case TrangThaiDeXuatNhuCau.CVVPTraLai:
                return { label: "CV VP trả lại", color: "orange" };
            case TrangThaiDeXuatNhuCau.TrinhPhoCVP:
                return { label: "Trình Phó CVP", color: "blue" };
            case TrangThaiDeXuatNhuCau.PhoCVPTraLai:
                return { label: "Phó CVP trả lại", color: "orange" };
            case TrangThaiDeXuatNhuCau.TrinhCVP:
                return { label: "Trình CVP", color: "blue" };
            case TrangThaiDeXuatNhuCau.CVPTraLai:
                return { label: "CVP trả lại", color: "orange" };
            case TrangThaiDeXuatNhuCau.TrinhPhoGiamDoc:
                return { label: "Trình Phó GĐ", color: "blue" };
            case TrangThaiDeXuatNhuCau.PhoGiamDocTraLai:
                return { label: "Phó GĐ trả lại", color: "orange" };
            case TrangThaiDeXuatNhuCau.TrinhGiamDoc:
                return { label: "Trình GĐ", color: "blue" };
            case TrangThaiDeXuatNhuCau.GiamDocTraLai:
                return { label: "GĐ trả lại", color: "orange" };
            case TrangThaiDeXuatNhuCau.PheDuyet:
                return { label: "Đã phê duyệt", color: "green" };
            default:
                return { label: "Không xác định", color: "default" };
        }
    };

    const isLanhDaoPhong = !!user?.isLanhDaoPhong && user?.idDonVi === data?.idDonVi;
    const isChanhVPLevel = user?.maChucVu === maChucVu.chanhVP;
    const isPhoCVP = user?.maChucVu === maChucVu.phoCVP && user?.idNhanVien === data?.idPhoCVP;
    const isChanhVP = user?.maChucVu === maChucVu.chanhVP && user?.idNhanVien === data?.idCVP;
    const isPhoGiamDoc = user?.maChucVu === maChucVu.phoGiamDoc && user?.idNhanVien === data?.idPhoGiamDoc;
    const isGiamDoc = user?.maChucVu === maChucVu.giamDoc && user?.idNhanVien === data?.idGiamDoc;

    const canApprove = isLanhDaoPhong || isPhoCVP || isChanhVP || isPhoGiamDoc || isGiamDoc;
    const canEdit = !!data && (data.idNVKhoiTao === user?.idNhanVien || data.idNVKhoiTao === user?.id) && [
        TrangThaiDeXuatNhuCau.KhoiTao,
        TrangThaiDeXuatNhuCau.ThuHoi,
        TrangThaiDeXuatNhuCau.LanhDaoPhongTraLai,
    ].includes(data.trangThai);
    const canEditMoTa = canEdit; // Chỉ cho phép sửa mô tả ở 3 trạng thái: Khởi tạo, Thu hồi, LĐ phòng trả lại
    // Kiểm tra CVVP bằng người được Chánh VP chọn cụ thể cho đợt đề xuất này (idCVVPThamMuu)
    const isCVVPAdvising = !!data && !!data.idCVVPThamMuu && user?.idNhanVien === data.idCVVPThamMuu && [TrangThaiDeXuatNhuCau.CVVPThamMuu, TrangThaiDeXuatNhuCau.CVVPTraLai].includes(data.trangThai);
    const canSubmit = !!data && (data.idNVKhoiTao === user?.idNhanVien || data.idNVKhoiTao === user?.id) && [
        TrangThaiDeXuatNhuCau.KhoiTao,
        TrangThaiDeXuatNhuCau.ThuHoi,
        TrangThaiDeXuatNhuCau.LanhDaoPhongTraLai,
        TrangThaiDeXuatNhuCau.ChanhVPTraLai,
    ].includes(data.trangThai);

    const canChanhVPTransfer = !!data && isChanhVPLevel && data.trangThai === TrangThaiDeXuatNhuCau.ChuyenChanhVP;

    const shouldShowApproveButton = () => {
        if (!canApprove || !data) return false;

        if (isLanhDaoPhong && data.idDonVi == user?.idDonVi && data.trangThai === TrangThaiDeXuatNhuCau.TrinhLanhDaoPhong) return true;
        if (isPhoCVP && (data.idPhoCVP || 0) > 0 && data.trangThai === TrangThaiDeXuatNhuCau.TrinhPhoCVP) return true;
        if (isChanhVP && (data.idCVP || 0) > 0 && data.trangThai === TrangThaiDeXuatNhuCau.TrinhCVP) return true;
        if (isPhoGiamDoc && (data.idPhoGiamDoc || 0) > 0 && data.trangThai === TrangThaiDeXuatNhuCau.TrinhPhoGiamDoc) return true;
        if (isGiamDoc && (data.idGiamDoc || 0) > 0 && data.trangThai === TrangThaiDeXuatNhuCau.TrinhGiamDoc) return true;

        return false;
    };

    const handlePheDuyet = async (lyDo: string) => {
        try {
            const response = await pheDuyetDeXuat(data!.id!, lyDo);
            if (response.trangThai === responseStatus.thanhCong) {
                toast.success("Phê duyệt thành công");
                setModalOpen(false);
                loadData();
            } else {
                toast.error(response.thongBao || "Phê duyệt không thành công");
            }
        } catch (error: any) {
            toast.error(error.message || "Lỗi khi phê duyệt");
        }
    };

    const handleTuChoi = async (lyDo: string) => {
        try {
            const response = await tuChoiDeXuat(data!.id!, lyDo);
            if (response.trangThai === responseStatus.thanhCong) {
                toast.success("Từ chối thành công");
                setModalOpen(false);
                loadData();
            } else {
                toast.error(response.thongBao || "Từ chối không thành công");
            }
        } catch (error: any) {
            toast.error(error.message || "Lỗi khi từ chối");
        }
    };

    const handleThuHoi = async () => {
        try {
            const response = await thuHoiDeXuat(data!.id!);
            if (response.trangThai === responseStatus.thanhCong) {
                toast.success("Thu hồi thành công");
                loadData();
            } else {
                toast.error(response.thongBao || "Thu hồi không thành công");
            }
        } catch (error: any) {
            toast.error(error.message || "Lỗi khi thu hồi");
        }
    };

    const handleTrinhDuyet = async () => {
        try {
            const response = await trinhDuyetDeXuat(data!.id!);
            if (response.trangThai === responseStatus.thanhCong) {
                toast.success("Trình lãnh đạo phòng thành công");
                loadData();
            } else {
                toast.error(response.thongBao || "Trình lãnh đạo phòng không thành công");
            }
        } catch (error: any) {
            toast.error(error.message || "Lỗi khi trình lãnh đạo phòng");
        }
    };

    const handleOpenChuyenCVVP = () => {
        setSelectedCVVP(undefined);
        setCVVPModalOpen(true);
    };

    const handleConfirmChuyenCVVP = async () => {
        if (!selectedCVVP) {
            toast.error("Vui lòng chọn chuyên viên văn phòng tham mưu");
            return;
        }
        try {
            setIsTransferringCVVP(true);
            const response = await chuyenCVVPDeXuat(data!.id!, selectedCVVP);
            if (response.trangThai === responseStatus.thanhCong) {
                toast.success("Chuyển CV VP tham mưu thành công");
                setCVVPModalOpen(false);
                loadData();
            } else {
                toast.error(response.thongBao || "Chuyển không thành công");
            }
        } catch (error: any) {
            toast.error(error.message || "Lỗi khi chuyển CV VP");
        } finally {
            setIsTransferringCVVP(false);
        }
    };

    const handleSaveAdvisory = async (sendForApproval: boolean = false) => {
        try {
            setIsSavingAdvisory(true);
            const payload: any = {
                id: data?.id,
                loaiNhuCau: data?.loaiNhuCau,
                idTaiSan: data?.idTaiSan,
                tenTaiSan: data?.tenTaiSan,
                soLuong: data?.soLuong,
                donViTinh: data?.donViTinh,
                tinhTrangTaiSan: data?.tinhTrangTaiSan,
                phanLoaiTaiSan: data?.phanLoaiTaiSan,
                moTa: data?.moTa,
                noiDungThamMuu: advisoryContent,
                idPhoCVP: phoCVPChecked && phoCVPInfo ? phoCVPInfo.id : 0,
                idCVP: cvpChecked && cvpInfo ? cvpInfo.id : 0,
                idPhoGiamDoc: phoGDChecked && phoGDInfo ? phoGDInfo.id : 0,
                idGiamDoc: gdChecked && gdInfo ? gdInfo.id : 0,
            };

            const response = await luuDeXuat(payload);
            if (response.trangThai === responseStatus.thanhCong) {
                toast.success("Lưu tham mưu thành công");
                if (sendForApproval) {
                    // Trình đến cấp duyệt tiếp theo (Phó CVP)
                    handleSendAdvisoryToApprove();
                } else {
                    loadData();
                }
            } else {
                toast.error(response.thongBao || "Lưu không thành công");
            }
        } catch (error: any) {
            toast.error(error.message || "Lỗi khi lưu tham mưu");
        } finally {
            setIsSavingAdvisory(false);
        }
    };

    const handleSendAdvisoryToApprove = async () => {
        try {
            const payload: any = {
                id: data?.id,
                loaiNhuCau: data?.loaiNhuCau,
                idTaiSan: data?.idTaiSan,
                tenTaiSan: data?.tenTaiSan,
                soLuong: data?.soLuong,
                donViTinh: data?.donViTinh,
                tinhTrangTaiSan: data?.tinhTrangTaiSan,
                phanLoaiTaiSan: data?.phanLoaiTaiSan,
                moTa: data?.moTa,
                noiDungThamMuu: advisoryContent,
                idPhoCVP: phoCVPChecked && phoCVPInfo ? phoCVPInfo.id : 0,
                idCVP: cvpChecked && cvpInfo ? cvpInfo.id : 0,
                idPhoGiamDoc: phoGDChecked && phoGDInfo ? phoGDInfo.id : 0,
                idGiamDoc: gdChecked && gdInfo ? gdInfo.id : 0,
            };

            const response = await luuDeXuat(payload);
            if (response.trangThai === responseStatus.thanhCong) {
                // Sau lưu advisory, trình sang cấp duyệt tiếp theo
                const trinhResponse = await trinhThamMuuDeXuat(data!.id!);
                if (trinhResponse.trangThai === responseStatus.thanhCong) {
                    toast.success("Lưu tham mưu và trình duyệt thành công");
                    loadData();
                } else {
                    toast.error(trinhResponse.thongBao || "Trình duyệt không thành công");
                }
            } else {
                toast.error(response.thongBao || "Lưu không thành công");
            }
        } catch (error: any) {
            toast.error(error.message || "Lỗi khi lưu và trình tham mưu");
        } finally {
            setIsSavingAdvisory(false);
        }
    };

    if (loading) {
        return <LoadingDetailPage title="Chi tiết đề xuất nhu cầu tài sản" />;
    }

    if (!data) {
        return (
            <section>
                <ContentHeader title="Chi tiết đề xuất nhu cầu tài sản" />
                <div className="card">
                    <div className="card-body" style={{ textAlign: "center" }}>
                        Không tìm thấy thông tin
                    </div>
                </div>
            </section>
        );
    }

    const levelCount = 2 + [
        (data.idPhoCVP || 0) > 0,
        (data.idCVP || 0) > 0,
        (data.idPhoGiamDoc || 0) > 0,
        (data.idGiamDoc || 0) > 0,
    ].filter(Boolean).length;

    return (
        <section>
            <ContentHeader title="Chi tiết đề xuất nhu cầu tài sản" />
            <Row>
                <Col span={24}>
                    <Card>
                        <Row gutter={[16, 16]} justify="space-between" align="middle">
                            <Col>
                                <h5 className="mb-0">Thông tin đề xuất</h5>
                            </Col>
                            <Col>
                                <Tag color={getTrangThaiInfo(data.trangThai).color}>
                                    {getTrangThaiInfo(data.trangThai).label}
                                </Tag>
                            </Col>
                        </Row>

                        {data.lyDoTraLai && ((data.trangThai == TrangThaiDeXuatNhuCau.LanhDaoPhongTraLai) || (data.trangThai == TrangThaiDeXuatNhuCau.KhoiTao) || (data.trangThai == TrangThaiDeXuatNhuCau.ThuHoi)) && (
                            <Alert
                                message="Lý do trả lại"
                                description={data.lyDoTraLai}
                                type="warning"
                                showIcon
                                style={{ marginTop: 16, marginBottom: 16 }}
                            />
                        )}

                        <Descriptions bordered column={2} style={{ marginTop: 16 }}>
                            <Descriptions.Item label="Loại nhu cầu">
                                <Tag color="blue" style={{ fontSize: 13 }}>{getTenLoaiNhuCau(data.loaiNhuCau)}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Trạng thái">
                                <Tag color={getTrangThaiInfo(data.trangThai).color}>
                                    {getTrangThaiInfo(data.trangThai).label}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Tên tài sản" span={2}>
                                <b>{data.tenTaiSan || "-"}</b>
                                {data.maTaiSan && <Tag color="cyan" style={{ marginLeft: 8 }}>Mã: {data.maTaiSan}</Tag>}
                            </Descriptions.Item>
                            <Descriptions.Item label="Phân loại">
                                {data.tenPhanLoaiTaiSan || (data.phanLoaiTaiSan === 3 ? "Công cụ dụng cụ (CCDC)" : data.phanLoaiTaiSan === 1 ? "Tài sản cố định (TSCĐ)" : "-")}
                            </Descriptions.Item>
                            <Descriptions.Item label="Số lượng & ĐVT">
                                {data.soLuong || 1} {data.donViTinh ? `(${data.donViTinh})` : ""}
                            </Descriptions.Item>
                            {data.tinhTrangTaiSan && (
                                <Descriptions.Item label="Tình trạng tài sản" span={2}>
                                    {data.tinhTrangTaiSan}
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label="Cá nhân đăng ký">
                                {data.hoTenCaNhan || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Đơn vị">
                                {data.tenDonVi || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Ghi chú" span={2}>
                                {canEditMoTa ? (
                                    <Input.TextArea
                                        value={data.moTa || ""}
                                        onChange={(e) => {
                                            setData({ ...data, moTa: e.target.value });
                                        }}
                                        placeholder="Nhập ghi chú"
                                        rows={3}
                                    />
                                ) : (
                                    data.moTa || "-"
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Ngày tạo">
                                {data.ngayKhoiTao ? dateCommon.formatDate(data.ngayKhoiTao) : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Ngày phê duyệt">
                                {data.ngayDuyet ? dateCommon.formatDate(data.ngayDuyet) : "-"}
                            </Descriptions.Item>
                        </Descriptions>

                        <Descriptions bordered column={2} style={{ marginTop: 16 }}>
                            <Descriptions.Item label="CV VP tham mưu">
                                {data.tenCVVPThamMuu || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Phó CVP">
                                {data.tenPhoCVP || "-"}{" "}
                                {(data.idPhoCVP || 0) > 0
                                    ? data.trangThai >= TrangThaiDeXuatNhuCau.TrinhCVP
                                        ? <Tag color="green">Đã duyệt</Tag>
                                        : <Tag color="warning">Chờ duyệt</Tag>
                                    : null}
                            </Descriptions.Item>
                            <Descriptions.Item label="Chánh VP">
                                {data.tenCVP || "-"}{" "}
                                {(data.idCVP || 0) > 0
                                    ? data.trangThai >= TrangThaiDeXuatNhuCau.TrinhPhoGiamDoc
                                        ? <Tag color="green">Đã duyệt</Tag>
                                        : <Tag color="warning">Chờ duyệt</Tag>
                                    : null}
                            </Descriptions.Item>
                            <Descriptions.Item label="Phó Giám đốc">
                                {data.tenPhoGiamDoc || "-"}{" "}
                                {(data.idPhoGiamDoc || 0) > 0
                                    ? data.trangThai >= TrangThaiDeXuatNhuCau.TrinhGiamDoc
                                        ? <Tag color="green">Đã duyệt</Tag>
                                        : <Tag color="warning">Chờ duyệt</Tag>
                                    : null}
                            </Descriptions.Item>
                            <Descriptions.Item label="Giám đốc">
                                {data.tenGiamDoc || "-"}{" "}
                                {(data.idGiamDoc || 0) > 0
                                    ? data.trangThai === TrangThaiDeXuatNhuCau.PheDuyet
                                        ? <Tag color="green">Đã duyệt</Tag>
                                        : <Tag color="warning">Chờ duyệt</Tag>
                                    : null}
                            </Descriptions.Item>
                        </Descriptions>

                        {data.noiDungThamMuu && (
                            <Descriptions bordered column={1} style={{ marginTop: 16 }}>
                                <Descriptions.Item label="Nội dung tham mưu (CVVP)">
                                    {data.noiDungThamMuu}
                                </Descriptions.Item>
                            </Descriptions>
                        )}

                        {isCVVPAdvising && (
                            <div style={{ marginTop: 16, padding: 16, border: '1px solid #d9d9d9', borderRadius: 4 }}>
                                <h4>Tham mưu và Cấu hình Duyệt</h4>
                                <Form layout="vertical">
                                    <Form.Item label="Nội dung tham mưu">
                                        <Input.TextArea
                                            value={advisoryContent}
                                            onChange={(e) => setAdvisoryContent(e.target.value)}
                                            placeholder="Nhập nội dung tham mưu"
                                            rows={4}
                                        />
                                    </Form.Item>
                                    <Form.Item label="Cấu hình các cấp duyệt">
                                        <Space direction="vertical">
                                            <Checkbox
                                                checked={phoCVPChecked}
                                                onChange={(e) => setPhoCVPChecked(e.target.checked)}
                                            >
                                                Phó CVP {phoCVPInfo ? `(${phoCVPInfo.hoTen})` : ""}
                                            </Checkbox>
                                            <Checkbox
                                                checked={cvpChecked}
                                                onChange={(e) => setCVPChecked(e.target.checked)}
                                            >
                                                CVP {cvpInfo ? `(${cvpInfo.hoTen})` : ""}
                                            </Checkbox>
                                            <Checkbox
                                                checked={phoGDChecked}
                                                onChange={(e) => setPhoGDChecked(e.target.checked)}
                                            >
                                                Phó Giám đốc {phoGDInfo ? `(${phoGDInfo.hoTen})` : ""}
                                            </Checkbox>
                                            <Checkbox
                                                checked={gdChecked}
                                                onChange={(e) => setGDChecked(e.target.checked)}
                                            >
                                                Giám đốc {gdInfo ? `(${gdInfo.hoTen})` : ""}
                                            </Checkbox>
                                        </Space>
                                    </Form.Item>
                                </Form>
                            </div>
                        )}

                        <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
                            {data.trangThai === TrangThaiDeXuatNhuCau.PheDuyet && (
                                <Col>
                                    <Button
                                        type="primary"
                                        icon={<PrinterOutlined />}
                                        loading={isPrinting}
                                        onClick={handleClickInButton}
                                    >
                                        In
                                    </Button>
                                </Col>
                            )}

                            {data.trangThai === TrangThaiDeXuatNhuCau.PheDuyet && data.duongDanFileIn && (
                                <Col>
                                    <Button
                                        href={`${urlAdmin}${(data.duongDanFileIn || "").replace(/\\/g, "/")}`}
                                        target="_blank"
                                    >
                                        Xem file đã in
                                    </Button>
                                </Col>
                            )}

                            {shouldShowApproveButton() && (
                                <>
                                    <Col>
                                        <Button
                                            type="primary"
                                            onClick={() => {
                                                setCurrentAction("approve");
                                                setModalTitle("Phê duyệt");
                                                setModalOpen(true);
                                            }}
                                        >
                                            Phê duyệt
                                        </Button>
                                    </Col>
                                    <Col>
                                        <Button
                                            danger
                                            onClick={() => {
                                                setCurrentAction("reject");
                                                setModalTitle("Từ chối");
                                                setModalOpen(true);
                                            }}
                                        >
                                            Từ chối
                                        </Button>
                                    </Col>
                                </>
                            )}

                            {canChanhVPTransfer && (
                                <>
                                    <Col>
                                        <Button type="primary" icon={<SendOutlined />} onClick={handleOpenChuyenCVVP}>
                                            Chuyển CV VP
                                        </Button>
                                    </Col>
                                    <Col>
                                        <Button
                                            danger
                                            onClick={() => {
                                                setCurrentAction("reject");
                                                setModalTitle("Từ chối");
                                                setModalOpen(true);
                                            }}
                                        >
                                            Từ chối
                                        </Button>
                                    </Col>
                                </>
                            )}

                            {isCVVPAdvising && (
                                <>
                                    <Col>
                                        <Button
                                            type="default"
                                            icon={<SaveOutlined />}
                                            onClick={() => handleSaveAdvisory(false)}
                                            loading={isSavingAdvisory}
                                        >
                                            Lưu
                                        </Button>
                                    </Col>
                                    <Col>
                                        <Button
                                            type="primary"
                                            onClick={() => handleSaveAdvisory(true)}
                                            loading={isSavingAdvisory}
                                        >
                                            Lưu và Trình
                                        </Button>
                                    </Col>
                                    <Col>
                                        <Button
                                            danger
                                            onClick={() => {
                                                setCurrentAction("reject");
                                                setModalTitle("Từ chối");
                                                setModalOpen(true);
                                            }}
                                        >
                                            Từ chối
                                        </Button>
                                    </Col>
                                </>
                            )}

                            {canSubmit && (
                                <Col>
                                    <Button type="primary" icon={<SendOutlined />} onClick={handleTrinhDuyet}>
                                        Trình lãnh đạo phòng
                                    </Button>
                                </Col>
                            )}

                            {!!data && (data.idNVKhoiTao === user?.idNhanVien || data.idNVKhoiTao === user?.id) &&
                                [TrangThaiDeXuatNhuCau.KhoiTao, TrangThaiDeXuatNhuCau.TrinhLanhDaoPhong].includes(data.trangThai) && (
                                    <Col>
                                        <Button icon={<UndoOutlined />} onClick={handleThuHoi}>
                                            Thu hồi
                                        </Button>
                                    </Col>
                                )}

                            {canEdit && (
                                <Col>
                                    <Button
                                        icon={<EditOutlined />}
                                        onClick={() => navigate(`${Links.DeXuatNhuCauTaiSan.ChiTiet}?id=${data.id}`)}
                                    >
                                        Sửa
                                    </Button>
                                </Col>
                            )}

                            <Col>
                                <Button danger icon={<CloseOutlined />} onClick={() => navigate(-1)}>
                                    Đóng
                                </Button>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>

            <ModalTextForm
                open={modalOpen}
                title={modalTitle}
                placeholder={currentAction === "reject" ? "Nhập lý do từ chối" : "Nhập ý kiến (nếu có)"}
                batBuocLyDo={currentAction === "reject"}
                onOk={(value) => {
                    if (currentAction === "approve") {
                        handlePheDuyet(value);
                    } else if (currentAction === "reject") {
                        handleTuChoi(value);
                    }
                }}
                onCancel={() => {
                    setModalOpen(false);
                    setCurrentAction(null);
                }}
            />

            <Modal
                open={cvvpModalOpen}
                title="Chọn chuyên viên văn phòng tham mưu"
                onOk={handleConfirmChuyenCVVP}
                onCancel={() => setCVVPModalOpen(false)}
                confirmLoading={isTransferringCVVP}
                okText="Chuyển"
                cancelText="Hủy"
            >
                <div style={{ marginBottom: 8 }}>Chuyên viên văn phòng:</div>
                <NhanVienPhongBanSelectBox
                    idDonVi={ID_DON_VI_VAN_PHONG}
                    noLanhDao
                    value={selectedCVVP}
                    onChange={(e: any) => setSelectedCVVP(e.target.value)}
                />
            </Modal>

            {/* Bản in ẩn dùng để capture */}
            <div style={{ position: "fixed", top: -10000, left: -10000 }}>
                <div
                    ref={printRef}
                    className="document"
                    style={{
                        boxSizing: "border-box",
                        padding: "20mm 20mm 20mm 20mm",
                        width: "210mm",
                        minHeight: "297mm",
                        background: "#fff",
                        fontFamily: "'Times New Roman', Times, serif",
                        fontSize: 15,
                        color: "#000",
                        lineHeight: 1.5,
                    }}
                >
                    <Row gutter={8}>
                        <Col span={12}>
                            <div style={{ textAlign: "center" }}>
                                <b style={{ fontSize: 15 }}>QUỸ ĐẦU TƯ PHÁT TRIỂN</b>
                                <br />
                                <b style={{ fontSize: 15 }}>{(data.tenDonVi || "").toUpperCase()}</b>
                                <hr style={{ borderTop: "1px solid #000", width: "60%", margin: "4px auto 0" }} />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ textAlign: "center" }}>
                                <b style={{ fontSize: 15 }}>CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM</b>
                                <div>Độc lập - Tự do - Hạnh phúc</div>
                                <hr style={{ borderTop: "1px solid #000", width: "50%", margin: "4px auto 0" }} />
                            </div>
                        </Col>
                    </Row>

                    <div style={{ textAlign: "center", marginTop: 24, marginBottom: 24 }}>
                        <h2 style={{ margin: 0, fontWeight: "bold", fontSize: 20 }}>ĐỀ XUẤT NHU CẦU TÀI SẢN</h2>
                        {data.maTaiSan && <div style={{ fontStyle: "italic", marginTop: 4 }}>Mã: {data.maTaiSan}</div>}
                    </div>

                    <div style={{ marginBottom: 12 }}>
                        Kính gửi: <b>Lãnh đạo Quỹ đầu tư phát triển thành phố Đà Nẵng</b>
                    </div>

                    <div style={{ marginBottom: 8 }}>
                        <b>I. Thông tin đề xuất:</b>
                    </div>
                    <div style={{ marginLeft: 20 }}>
                        <div>1. Người đề xuất: {data.hoTenCaNhan}</div>
                        <div>2. Phòng ban: {data.tenDonVi}</div>
                        <div>3. Loại nhu cầu: {getTenLoaiNhuCau(data.loaiNhuCau)}</div>
                        <div>4. Tên tài sản: {data.tenTaiSan}</div>
                        <div>5. Số lượng: {data.soLuong} {data.donViTinh}</div>
                        <div>6. Mô tả/Nội dung đề xuất: {data.moTa || "-"}</div>
                    </div>

                    {data.noiDungThamMuu && (
                        <>
                            <div style={{ marginTop: 20, marginBottom: 8 }}>
                                <b>II. Nội dung tham mưu của chuyên viên văn phòng:</b>
                            </div>
                            <div style={{ marginLeft: 20 }}>
                                <div style={{ whiteSpace: "pre-wrap" }}>{data.noiDungThamMuu}</div>
                                {data.tenCVVPThamMuu && (
                                    <div style={{ marginTop: 6 }}>Chuyên viên tham mưu: {data.tenCVVPThamMuu}</div>
                                )}
                            </div>
                        </>
                    )}

                    <Row gutter={8} style={{ marginTop: 32, textAlign: "center" }}>
                        <Col span={24 / levelCount}>
                            <b>NGƯỜI TẠO</b>
                            <div style={{ marginTop: 64 }}>{data.hoTenCaNhan}</div>
                            {data.ngayTrinh && <div style={{ fontStyle: "italic" }}>Đã ký ngày {dateCommon.formatDate(data.ngayTrinh)}</div>}
                        </Col>
                        <Col span={24 / levelCount}>
                            <b>LÃNH ĐẠO PHÒNG</b>
                            <div style={{ marginTop: 64 }}>{data.tenLanhDaoPhong}</div>
                            {data.ngayDuyetLanhDaoPhong && <div style={{ fontStyle: "italic" }}>Đã ký ngày {dateCommon.formatDate(data.ngayDuyetLanhDaoPhong)}</div>}
                        </Col>
                        {(data.idPhoCVP || 0) > 0 && (
                            <Col span={24 / levelCount}>
                                <b>PHÓ CHÁNH VĂN PHÒNG</b>
                                <div style={{ marginTop: 64 }}>{data.tenPhoCVP}</div>
                                {data.ngayDuyetPhoCVP && <div style={{ fontStyle: "italic" }}>Đã ký ngày {dateCommon.formatDate(data.ngayDuyetPhoCVP)}</div>}
                            </Col>
                        )}
                        {(data.idCVP || 0) > 0 && (
                            <Col span={24 / levelCount}>
                                <b>CHÁNH VĂN PHÒNG</b>
                                <div style={{ marginTop: 64 }}>{data.tenCVP}</div>
                                {data.ngayDuyetCVP && <div style={{ fontStyle: "italic" }}>Đã ký ngày {dateCommon.formatDate(data.ngayDuyetCVP)}</div>}
                            </Col>
                        )}
                        {(data.idPhoGiamDoc || 0) > 0 && (
                            <Col span={24 / levelCount}>
                                <b>PHÓ GIÁM ĐỐC</b>
                                <div style={{ marginTop: 64 }}>{data.tenPhoGiamDoc}</div>
                                {data.ngayDuyetPhoGiamDoc && <div style={{ fontStyle: "italic" }}>Đã ký ngày {dateCommon.formatDate(data.ngayDuyetPhoGiamDoc)}</div>}
                            </Col>
                        )}
                        {(data.idGiamDoc || 0) > 0 && (
                            <Col span={24 / levelCount}>
                                <b>GIÁM ĐỐC</b>
                                <div style={{ marginTop: 64 }}>{data.tenGiamDoc}</div>
                                {data.ngayDuyetGiamDoc && <div style={{ fontStyle: "italic" }}>Đã ký ngày {dateCommon.formatDate(data.ngayDuyetGiamDoc)}</div>}
                            </Col>
                        )}
                    </Row>

                    <div style={{ marginTop: 48, fontSize: 13, fontStyle: "italic", textAlign: "center" }}>
                        Bản in chuyển đổi từ Phần mềm HRM ngày {dateCommon.formatDate(new Date())}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default XemDeXuatNhuCau;
